// ATS embed bridge.
//
// When AI Interview is embedded as a cross-origin iframe inside a client's ATS
// (e.g. netflix.com embedding interview.vriti.ai), the two pages don't share
// localStorage, so the client's page must hand us the recruiter's token. It does
// that with postMessage; this module receives the client token and hands it to
// the hook (use-ats-embed), which exchanges it for a genuine IAM token.
//
// Everything here is a no-op unless a `connectorId` is configured, so standalone
// and Vriti flows are completely unaffected.
// postMessage protocol between the client's host page and the embedded iframe.
export const ATS_MSG = {
    // iframe -> parent: "I'm mounted, send me the auth."
    READY: 'ATS_AI_INTERVIEW_READY',
    // parent -> iframe: { token, connectorId? }
    AUTH: 'ATS_AI_INTERVIEW_AUTH',
    // iframe -> parent: "token stored."
    AUTH_ACK: 'ATS_AI_INTERVIEW_AUTH_ACK',
};
/** True when running inside a (cross-origin) parent frame. */
export const isEmbedded = () => typeof window !== 'undefined' && window.self !== window.top;
/**
 * Connector id for this embed session.
 *
 * Read from the iframe URL (?ats_connector=...) and PERSISTED in sessionStorage,
 * so it survives internal redirects that drop the query string — e.g. loading a
 * protected deep-link (`/schedule-interview`) bounces through `/login`, losing
 * `?ats_connector`. Persisting it keeps the embed active across that redirect.
 */
export const connectorIdFromUrl = () => {
    if (typeof window === 'undefined')
        return '';
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('ats_connector');
        if (fromUrl) {
            sessionStorage.setItem('ats_connector', fromUrl);
            return fromUrl;
        }
        return sessionStorage.getItem('ats_connector') || '';
    }
    catch (_a) {
        return '';
    }
};
/**
 * True when this page is an active ATS embed (in an iframe AND a connector id is
 * present via URL). Used to skip standalone-only behaviour (e.g. the subscription
 * interceptor, which gates on the standalone app's identity and blanks the embed).
 */
export const isAtsEmbedActive = () => isEmbedded() && !!connectorIdFromUrl();
// Origins allowed to seed the token — comma-separated REACT_APP_ATS_ALLOWED_ORIGINS.
// '*' disables the check (LOCAL DEV ONLY — with '*' any site could inject a token).
const allowedOrigins = () => (process.env.REACT_APP_ATS_ALLOWED_ORIGINS || '')
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean);
const isOriginAllowed = (origin) => {
    const list = allowedOrigins();
    if (list.includes('*'))
        return true;
    return list.includes(origin);
};
// The currently-registered message handler (module-level so a re-mount replaces
// rather than stacks listeners). React can mount the new App before unmounting
// the old one on a route swap, so we always re-register + re-announce READY.
let currentHandler = null;
/**
 * Install the postMessage receiver. Returns a cleanup function.
 *
 * No-op (returns a no-op cleanup) unless embedded in a parent frame AND a
 * connectorId is configured — so it never touches standalone / Vriti flows.
 */
export const initAtsEmbedReceiver = (opts = {}) => {
    var _a;
    const noop = () => { };
    if (typeof window === 'undefined')
        return noop;
    if (!opts.connectorId)
        return noop; // not an ATS embed
    if (!isEmbedded())
        return noop; // not in an iframe
    const handler = (event) => {
        var _a, _b;
        const data = (event.data || {});
        if (!isOriginAllowed(event.origin)) {
            // Dropping the token silently is the single hardest failure to diagnose in
            // this integration: the client sees READY, sends AUTH, gets no reply and no
            // error, and the recruiter simply never signs in. Say so — but only for a
            // message that was clearly meant for us, so browser extensions and other
            // frames on the page don't produce noise.
            if (data.type === ATS_MSG.AUTH) {
                // eslint-disable-next-line no-console
                console.error(`[ats-embed] Ignored ${ATS_MSG.AUTH} from "${event.origin}": that origin is ` +
                    `not in REACT_APP_ATS_ALLOWED_ORIGINS (currently ` +
                    `${JSON.stringify(allowedOrigins())}). The recruiter will NOT be signed in. ` +
                    `This is baked in at build time — add the host's exact origin and rebuild.`);
            }
            return;
        }
        if (data.type !== ATS_MSG.AUTH || !data.token)
            return;
        (_a = opts.onAuth) === null || _a === void 0 ? void 0 : _a.call(opts, data.token, data.connectorId || opts.connectorId || '');
        // Ack back to the exact origin that sent it.
        try {
            (_b = event.source) === null || _b === void 0 ? void 0 : _b.postMessage({ type: ATS_MSG.AUTH_ACK }, event.origin);
        }
        catch (_c) {
            /* best-effort */
        }
    };
    // Replace any previously-registered handler (a re-mount uses the latest opts).
    if (currentHandler)
        window.removeEventListener('message', currentHandler);
    currentHandler = handler;
    window.addEventListener('message', handler);
    // Tell the parent we're ready to receive the token. The READY ping carries no
    // secrets, so '*' target origin is fine; the sensitive token flows the other
    // way and is origin-checked on arrival. Always (re)announce so a freshly-mounted
    // embed (e.g. after the landing→dashboard route swap) gets a fresh token.
    try {
        (_a = window.parent) === null || _a === void 0 ? void 0 : _a.postMessage({ type: ATS_MSG.READY }, '*');
    }
    catch (_b) {
        /* best-effort */
    }
    return () => {
        window.removeEventListener('message', handler);
        if (currentHandler === handler)
            currentHandler = null;
    };
};
